import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAppleSignIn } from "@/hooks/useAppleSignIn";
import { toast } from "sonner";
import api from "@/lib/api";
import { consumePostAuthRedirect } from "@/lib/post-auth-redirect";
import { getPlatform } from "@/lib/nativeBridge";

/**
 * AppleSignInButton — renders a Sign in with Apple button that follows
 * Apple's Human Interface Guidelines: the official Apple logo mark,
 * system font, black fill with white content, approved title variants.
 * (Guideline 4 — third-party icon-library logos are not allowed.)
 *
 * Hidden entirely when SIWA isn't configured on the backend (the
 * useAppleSignIn hook polls /api/auth/apple/config and reports back).
 *
 * Props:
 *   - mode: "login" | "register" — only changes the label text
 */

// Official Apple logo mark (vector path per Apple's sign-in button artwork).
const AppleLogoMark = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    focusable="false"
    style={{ marginTop: -2 }}
  >
    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.03 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.56-1.702z" />
  </svg>
);

const SYSTEM_FONT =
  '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif';

export default function AppleSignInButton({ mode = "login" }) {
  const navigate = useNavigate();
  const { setUserAndToken } = useAuth();
  const { enabled, ready, signIn } = useAppleSignIn();
  const [busy, setBusy] = useState(false);

  if (!enabled) return null;

  const onClick = async () => {
    if (!ready || busy) return;
    setBusy(true);
    try {
      const payload = await signIn();
      if (!payload.identityToken) {
        toast.error("Apple sign-in did not return a token. Please try again.");
        return;
      }
      const { data } = await api.post("/auth/apple", {
        identity_token: payload.identityToken,
        authorization_code: payload.authorizationCode || null,
        email: payload.email || null,
        first_name: payload.firstName || null,
        last_name: payload.lastName || null,
        platform: getPlatform(),
      });
      if (data?.token) {
        setUserAndToken(data, data.token);
        try {
          const m = await import("@/lib/nativeAuth");
          await m.saveBiometricSession(data.token);
        } catch { /* web or old binary */ }
        toast.success(mode === "register" ? "Welcome to Armstrong K9 Registry" : "Welcome back");
        const next = consumePostAuthRedirect() || "/dashboard";
        navigate(next);
      }
    } catch (err) {
      // popup_closed_by_user is normal — don't surface as an error
      const code = err?.error || err?.code;
      if (code === "popup_closed_by_user" || code === "user_cancelled_authorize") {
        return;
      }
      const msg =
        err?.response?.data?.detail?.message ||
        err?.response?.data?.detail ||
        "Apple sign-in failed. Please try another method.";
      toast.error(typeof msg === "string" ? msg : "Apple sign-in failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!ready || busy}
      className="w-full rounded-full h-11 bg-black text-white hover:bg-[#1a1a1a] disabled:opacity-60 inline-flex items-center justify-center gap-2 transition-colors"
      style={{ fontFamily: SYSTEM_FONT, fontSize: 17, fontWeight: 500, letterSpacing: "-0.022em" }}
      data-testid={`${mode}-apple-btn`}
    >
      <AppleLogoMark size={18} />
      {busy
        ? "Connecting…"
        : mode === "register"
        ? "Sign up with Apple"
        : "Continue with Apple"}
    </button>
  );
}
